/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FirebaseAuthTypes } from "@react-native-firebase/auth"
import _ from "lodash"

import { ExerciseId } from "../common/CommonUserData"
import { Exercise } from "../common/Phase"
import { FirestoreSession } from "../features/models/Session"
import { AppUserData } from "../features/models/UserData"
/**
 * Eel = Exercise
 *
 * exercise with Array<eel> = dynamicExercise
 *    - dynamicId = exerciseId
 *    - id = eelId => this is the one use to create dailyPhase
 *
 * all callback are method to get firestore collection or document based on ids, pathology, phase or program
 *
 * */

/**
 * Check exercises from sessions to find validation
 * Use to return the first exercise if no validation
 *
 * return boolean
 * */
const checkIfAnyExerciseHasValidation = (sessions: FirestoreSession[]) =>
  _.some(sessions, session => {
    // Vérifier si la session a des exercices
    if (!session.exercises) {
      return false
    }
    const exerciseIds = Object.keys(session.exercises)
    // Vérifier si il y a des exercices
    if (exerciseIds.length === 0) {
      return false
    }
    return _.some(exerciseIds, id => _.has(session.exercises[id], "validation"))
  })

/**
 * Returns the current exercise (eel) to be used in the dynamic based on session information
 * - If no sessions or no exercises validated, returns the first eel
 * - Otherwise, returns the first non-validated exercise found
 * - If all exercises are validated, returns the last exercise
 */
const getDynamicExercise = (
  eelByIds: Record<ExerciseId, Exercise>,
  eels: ExerciseId[],
  sessions: FirestoreSession[],
): Exercise | undefined => {
  // Filtrer les eels pour ne garder que ceux qui existent dans eelByIds
  const validEels = eels.filter(id => eelByIds && eelByIds[id])

  // Si aucun eel valide, retourner undefined
  if (validEels.length === 0) {
    return undefined
  }

  // Vérifier si des sessions ont des validations
  const noExerciseHasValidation = !checkIfAnyExerciseHasValidation(sessions || [])

  // Si aucune session ou aucun exercice validé, retourner le premier
  if (_.isEmpty(sessions) || noExerciseHasValidation) {
    return eelByIds[validEels[0]]
  }

  // Identifier les exercices déjà validés
  const validatedExercises = {}
  sessions.forEach(session => {
    if (!session.exercises) {
      return
    }

    validEels.forEach(id => {
      if (session.exercises[id] && session.exercises[id].validation === true) {
        validatedExercises[id] = true
      }
    })
  })

  // Chercher le premier exercice non validé
  for (const id of validEels) {
    if (!validatedExercises[id]) {
      return eelByIds[id]
    }
  }

  // Si tous les exercices sont validés, retourner le dernier
  return eelByIds[validEels[validEels.length - 1]]
}

/**
 * Return the formattedExercises arrau of the current session
 * Method to regroup fields
 * Integrate eel inside the formattedExercises array based on previous validation
 * Otherwise return the basic list of exercises of there is no dynamic
 * */

interface FormattedExercisesParams {
  userData: Pick<AppUserData, "pathology" | "phase" | "program" | "specialty">
  user: Pick<FirebaseAuthTypes.User, "uid"> | null
  callbackToGetProgramSessions: (
    userData: Pick<AppUserData, "program" | "specialty">,
    user: Pick<FirebaseAuthTypes.User, "uid"> | null,
  ) => Promise<FirestoreSession[]>
  callbackToGetPhaseExercises: (
    userData: Pick<AppUserData, "phase">,
  ) => Promise<Record<ExerciseId, Exercise>>
  callbackToGetEelByIds: (
    userData: Pick<AppUserData, "pathology">,
  ) => Promise<Record<ExerciseId, Exercise>>
}

const getFormattedExercises = async ({
  userData,
  user,
  callbackToGetProgramSessions,
  callbackToGetPhaseExercises,
  callbackToGetEelByIds,
}: FormattedExercisesParams): Promise<Exercise[]> => {
  try {
    if (!userData.program && userData.specialty === "onco") {
      return []
    }
    const previousSessions = await callbackToGetProgramSessions?.(userData, user)
    const exerciseByIds = await callbackToGetPhaseExercises?.(userData)
    const exerciseIds = _.map(exerciseByIds, (ex, id) => id)
    const eelByIds = await callbackToGetEelByIds?.(userData)

    // Utiliser compact pour filtrer les undefined
    return _.compact(
      _.map(exerciseIds, id => {
        const ex = exerciseByIds?.[id]
        if (_.has(ex, "eels") && ex.eels) {
          const eel = getDynamicExercise(eelByIds, _.clone(ex.eels), previousSessions)
          // Si aucun exercice dynamique n'est trouvé, ne pas inclure cet exercice
          if (!eel) {
            return null
          }
          return {
            ...eel,
            id: eel.id,
            eels: ex.eels,
            dynamicId: ex.id,
          }
        }
        return ex
      }),
    ) as Exercise[]
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("getFormattedExercise error", e)
    throw new Error(e)
  }
}

export { getDynamicExercise, getFormattedExercises }
